/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import * as Sentry from '@sentry/react';
import { FC, useMemo } from 'react';
import { Navigate, Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';

/*
 * ------------------------------------------------------------------------------------------
 * - Internal Imports -
 * ------------------------------------------------------------------------------------------
 */
import AdminLayout from './components/layouts/AdminLayout';
import Layout from './components/layouts/Layout';
import { SetLayoutMode } from './components/layouts/SetLayout';
import AdminProvider from './components/providers/data/AdminProvider';
import BaseProvider from './components/providers/data/BaseProvider';
import { LayoutModeProvider } from './components/providers/data/LayoutProvider';
import SpeciesProvider from './components/providers/data/SpeciesProvider';
import UserGroupsProvider from './components/providers/data/UserGroupsProvider';
import SentryUserProvider from './components/providers/sentry/SentryUserProvider';
import AlertProvider from './components/providers/styles/AlertProvider';
import DefaultAdminThemeProvider from './components/providers/styles/DefaultAdminThemeProvider';
import DefaultThemeProvider from './components/providers/styles/DefaultThemeProvider';
import MapProvider from './components/providers/data/MapProvider';
import { LayoutMode } from './enums/LayoutMode';
import ChartsAndTablesPage from './pages/ChartsAndTablesPage';
import DataManagementPage from './pages/DataManagementPage';
import ErrorPage from './pages/ErrorPage';
import HomePage from './pages/HomePage';
import ManagementMapPage from './pages/ManagementMapPage';
import MapPage from './pages/MapPage';
import PhotoGalleryPage from './pages/PhotoGallery';
import ProfilePage from './pages/ProfilePage';
import RegisterGroupPage from './pages/RegisterGroupPage';
import SpeciesContentPage from './pages/SpeciesContentPage';
import InviteGroupMembersPage from './pages/groupAdmin/InviteGroupMembersPage';
import ManageGroupDocumentsPage from './pages/groupAdmin/ManageGroupDocumentsPage';
import ManageGroupMembersPage from './pages/groupAdmin/ManageGroupMembersPage';
import PageContentRedirect from './pages/redirects/ContentPageRedirect';
import CustomHomePageRedirect from './pages/redirects/CustomHomePageRedirect';
import DefaultPageRedirect from './pages/redirects/DefaultPageRedirect';
import LoginPageRedirect from './pages/redirects/LoginPageRedirect';
import MapPageRedirect from './pages/redirects/MapPageRedirect';
import ViewImagePageRedirect from './pages/redirects/ViewImagePageRedirect';
import MobileMapPageRedirect from './pages/redirects/MobileMapPageRedirect';
import SpeciesHomeRedirect from './pages/redirects/SpeciesHomePageRedirect';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';
import { AdminRoutes } from './routes/AdminRoutes';
import CommonProvider from './components/providers/data/CommonProvider';
import NotificationsPage from './pages/NotificationsPage';
import NotificationInformationPage from './pages/NotificationInformationPage';
import { NotificationProvider } from './components/providers/data/NotificationProvider';

/*
 * ------------------------------------------------------------------------------------------
 * - Exports -
 * ------------------------------------------------------------------------------------------
 */

export const App: FC = () => {
    const routes = useMemo(() => {
        const result: RouteObject[] = [

            // -- Home page
            {
                path: "/",
                element: (
                    <NotificationProvider>
                        <CommonProvider>
                            <SpeciesProvider>
                                <DefaultThemeProvider applySpeciesThemes>
                                    <AlertProvider>
                                        <SentryUserProvider>
                                            <UserGroupsProvider>
                                                <BaseProvider>
                                                    <LayoutModeProvider>
                                                        <MapProvider>
                                                            <Layout />
                                                        </MapProvider>
                                                    </LayoutModeProvider>
                                                </BaseProvider>
                                            </UserGroupsProvider>
                                        </SentryUserProvider>
                                    </AlertProvider>
                                </DefaultThemeProvider>
                            </SpeciesProvider>
                        </CommonProvider>
                    </NotificationProvider>
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <HomePage />
                            </>
                        )
                    },
                    {
                        path: 'mobile',
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to={'/'} />
                            },
                            {
                                path: 'map.aspx',
                                element: <MobileMapPageRedirect />
                            }
                        ]
                    },
                    {
                        path: 'newincursion',
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <Navigate replace to={'/newpests'} />
                            </>
                        )
                    },
                    {
                        path: 'camelscan',
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <Navigate replace to={'/otherpests'} />
                            </>
                        )
                    },
                    {
                        path: 'viewImage.aspx',
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <ViewImagePageRedirect />
                            </>
                        )
                    },
                    {
                        path: 'default.aspx',
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <Navigate replace to={'/'} />
                            </>
                        )
                    },
                    {
                        path: "error",
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <ErrorPage />
                            </>
                        )
                    },
                    {
                        path: "management",
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="wilddogscan/map" />
                            },
                            {
                                path: ":species",
                                children: [
                                    {
                                        index: true,
                                        element: <Navigate replace to="map" />
                                    },
                                    {
                                        path: 'map',
                                        element: (
                                            <>
                                                <SetLayoutMode mode={LayoutMode.Map} />
                                                <ManagementMapPage />
                                            </>
                                        )
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ":species",
                        element: (
                            <Outlet />
                        ),
                        children: [
                            {
                                index: true,
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <DefaultPageRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'map.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <MapPageRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'map',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Map} />
                                        <MapPage />
                                    </>
                                )
                            },
                            {
                                path: 'profile',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <ProfilePage />
                                    </>
                                )
                            },
                            {
                                path: 'notifications',
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <>
                                                <SetLayoutMode mode={LayoutMode.Default} />
                                                <NotificationsPage />
                                            </>
                                        )
                                    },
                                    {
                                        path: ":notificationId",
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <>
                                                        <SetLayoutMode mode={LayoutMode.Default} />
                                                        <NotificationInformationPage />
                                                    </>
                                                )
                                            },
                                        ]
                                    }
                                ]
                            },
                            {
                                path: 'default.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesHomeRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'pagecontent.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <PageContentRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'pagecontent',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <PageContentRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'content/:page',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesContentPage />
                                    </>
                                )
                            },
                            {
                                path: 'register.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../register" />
                                    </>
                                )
                            },
                            {
                                path: 'register',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesContentPage page='register'/>
                                    </>
                                )
                            },
                            {
                                path: 'forgotpassword.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../forgotpassword" />
                                    </>
                                )
                            },
                            {
                                path: 'forgotpassword',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesContentPage page='forgotpassword'/>
                                    </>
                                )
                            },
                            {
                                path: 'login.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <LoginPageRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'login',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <LoginPageRedirect />
                                    </>
                                )
                            },
                            {
                                path: 'requestdataaccess.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../requestdataaccess" />
                                    </>
                                )
                            },
                            {
                                path: 'requestdataaccess',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesContentPage page='requestdataaccess'/>
                                    </>
                                )
                            },
                            {
                                path: 'feedback.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../feedback" />
                                    </>
                                )
                            },
                            {
                                path: 'feedback',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <SpeciesContentPage page='feedback'/>
                                    </>
                                )
                            },
                            {
                                path: 'datamanagement.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../datamanagement" />
                                    </>
                                )
                            },
                            {
                                path: 'datamanagement',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <DataManagementPage />
                                    </>
                                )
                            },
                            {
                                path: 'chartsandtables',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <ChartsAndTablesPage />
                                    </>
                                )
                            },
                            {
                                path: 'photogallery',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <PhotoGalleryPage />
                                    </>
                                )
                            },
                            {
                                path: 'cgUserStatus.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../managegroupmembers" />
                                    </>
                                )
                            },
                            {
                                path: 'managegroupmembers',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <ManageGroupMembersPage />
                                    </>
                                )
                            },
                            {
                                path: 'managegroupdocuments',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <ManageGroupDocumentsPage />
                                    </>
                                )
                            },
                            {
                                path: 'cgInviteUsers.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <Navigate replace to="../invitegroupmembers" />
                                    </>
                                )
                            },
                            {
                                path: 'invitegroupmembers',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <InviteGroupMembersPage />
                                    </>
                                )
                            },
                            {
                                path: 'cgGroups.aspx',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <RegisterGroupPage />
                                    </>
                                )
                            },
                            {
                                path: 'nsw',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='nsw'/>
                                    </>
                                )
                            },
                            {
                                path: 'lls',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='lls'/>
                                    </>
                                )
                            },
                            {
                                path: 'kingisland',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='kingisland'/>
                                    </>
                                )
                            },
                            {
                                path: 'northernrivers',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='northernrivers'/>
                                    </>
                                )
                            },
                            {
                                path: 'phbg',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='phbg'/>
                                    </>
                                )
                            },
                            {
                                path: 'kangarooisland',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='kangarooisland'/>
                                    </>
                                )
                            },
                            {
                                path: 'brunyisland',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='brunyisland'/>
                                    </>
                                )
                            },
                            {
                                path: 'uppermurrumbidgee',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='uppermurrumbidgee'/>
                                    </>
                                )
                            },
                            {
                                path: 'sydneysouth',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='sydneysouth'/>
                                    </>
                                )
                            },
                            {
                                path: 'sydneynorth',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='sydneynorth'/>
                                    </>
                                )
                            },
                            {
                                path: 'menanglefox',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='menanglefox'/>
                                    </>
                                )
                            },
                            {
                                path: 'cimag',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='cimag'/>
                                    </>
                                )
                            },
                            {
                                path: 'mallee',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='mallee'/>
                                    </>
                                )
                            },
                            {
                                path: 'phillipisland',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='phillipisland'/>
                                    </>
                                )
                            },
                            {
                                path: 'northcoast',
                                element: (
                                    <>
                                        <SetLayoutMode mode={LayoutMode.Default} />
                                        <CustomHomePageRedirect route='northcoast'/>
                                    </>
                                )
                            }
                        ]
                    },
                    {
                        path: '*',
                        element: (
                            <>
                                <SetLayoutMode mode={LayoutMode.None} />
                                <ErrorPage />
                            </>
                        )
                    }
                ],
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'admin',
                element: (
                    <DefaultAdminThemeProvider>
                        <AdminProvider>
                            <AdminLayout />
                        </AdminProvider>
                    </DefaultAdminThemeProvider>
                ),
                children: AdminRoutes
            },
        ];

        return result;
    }, []);

    const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(routes);

    return (
        <RouterProvider router={router}/>
    );
}

export default App
